import { AnchorButton, Button, Classes, Icon, Intent, Tag, Tooltip, Callout } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import CX from 'classnames'
import * as Sentry from '@sentry/browser'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router'
import Client from '../../../client'
import { CopySnippet } from '../../../components/CopySnippet'
import { OrderDetails } from '../../../components/Drawer/orders/OrderDetails'
import { VariantDetails } from '../../../components/Drawer/VariantDetails'
import { InventoryUnitsDetails } from '../../../components/Drawer/InventoryUnitsDetails'
import EmptyPlaceholder from '../../../components/EmptyPlaceholder'
import { EventLogTimeline } from '../../../components/EventLogTimeline'
import InteractiveDatetime from '../../../components/InteractiveDatetime'
import Notes from '../../../components/Notes'
import PrintBarcodeButton from '../../../components/PrintBarcodeButton'
import { Spinner } from '../../../components/Spinner'
import config from '../../../config'
import { usePermissions } from '../../../hooks/usePermissions'
import { AppState } from '../../../store'
import NotFound from '../../not-found'
import CreateListingWhichAccountDialog from './CreateListingWhichAccountDialog'
import DuplicateDialog from './DuplicateDialog'
import { ListingHistorySection } from './ListingHistorySection'
import ProductTag from './ProductTag'
import { StrategySection } from './StrategySection'
import TagEditorDialog from './TagEditorDialog'
import { UprightStyle } from '../../../types'
import { getErrorString } from '../../../utils/serverErrorUtils'
import FullScreenErrorMessage from '../../../components/FullScreenErrorMessage'
import { formatDate } from '../../../utils'
import { getAutoListErrorMessage } from '../../../utils/autoListStatus'
import { DraftProductListActions } from './DraftProductListActions'

const productNotesStyle = {
  display: 'block'
}

const styles: UprightStyle = {
  itemHeader: { maxWidth: 'calc(50vw)' } // This must match the drawer size, usually Drawer.SIZE_STANDARD
}

const GridSquare = ({ title, contents }) => (
  <div>
    {title}
    <h4>
      <EmptyPlaceholder>{contents}</EmptyPlaceholder>
    </h4>
  </div>
)

const ManifestLink = ({ data }) => {
  const { includesSomePermissions } = usePermissions()

  // manifests.delete - Users with just `manifests.delete` access are able to view manifests.
  // So if they can view the product, also let them navigate to the manifest.
  return includesSomePermissions('manifests.edit', 'manifests.process', 'manifests.delete') ? (
    <Link to={`/manifests/${data.id}`}>{data.barcode}</Link>
  ) : (
    data.barcode
  )
}

export const ListingStrategyLink = ({ id, text }) => {
  const { permissions } = usePermissions()
  return id && permissions.includes('settings.strategies') ? (
    <Link to={`/admin/listing_strategies/${id}/edit`}>{text}</Link>
  ) : (
    text || 'Untitled'
  )
}

const ImageWithPlaceholder = ({ images, index, className }: { images: any[]; index: number; className?: string }) =>
  index < images.length && images[index].url ? (
    <img alt={`${index}`} src={images[index].url} className={className} />
  ) : (
    <div className={className}>
      <Icon icon={IconNames.MEDIA} iconSize={50} />
    </div>
  )

const ProductStatusTag = ({ product }) => {
  return (
    <>
      {product.recycledAt && (
        <Tag minimal intent={Intent.WARNING}>
          Recycled
        </Tag>
      )}

      {product.draft && <Tag minimal>Draft</Tag>}
      {product.autoListStatus === 'in_progress' && (
        <Tag minimal style={{ marginLeft: 4 }}>
          Auto-Listing in progress
        </Tag>
      )}
      {product.autoListStatus === 'failed' && (
        <Tag minimal intent={Intent.DANGER} style={{ marginLeft: 4 }}>
          Auto-Listing failed
        </Tag>
      )}
    </>
  )
}

interface Props {
  productId: number
  onRecycle?: Function
  onRestore?: Function
  onDelete?: Function
  onProductChange?: Function
  hideRecycleRestore?: boolean
  onDuplicate?: Function
}

function ProductView({
  productId,
  onRecycle,
  onRestore,
  onDelete,
  onProductChange,
  hideRecycleRestore,
  onDuplicate
}: Props) {
  const [product, setProduct] = useState<any>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notFound, setNotFound] = useState(false)
  const [tagDialogOpen, setTagDialogOpen] = useState(false)
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false)
  const [whichAccountDialogOpen, setWhichAccountDialogOpen] = useState(false)
  const [whichAccountMarketId, setWhichAccountMarketId] = useState(null)
  const [whichAccountAdditionalParams, setWhichAccountAdditionalParams] = useState(null)

  const reloadProduct = React.useCallback(
    async (setLoadingIndicator = true) => {
      setLoading(setLoadingIndicator)
      try {
        const res = await Client.getProductV6(productId, { includeFutureSteps: true })
        setProduct(res.data)
      } catch (e) {
        if (e.response && e.response.status === 404) {
          setNotFound(true)
        } else {
          setError(getErrorString(e, Sentry.captureException) || 'Could not load product, please try again')
        }
      } finally {
        setLoading(false)
      }
    },
    [productId]
  )

  React.useEffect(() => {
    reloadProduct()
  }, [reloadProduct])

  const concurrent = React.useMemo<boolean>(
    () =>
      (product.listingStrategyRun &&
        product.listingStrategyRun.currentStep &&
        product.listingStrategyRun.currentStep.stepType === 'CONCURRENT') ||
      (product.activeListings && product.activeListings.some(listing => listing.concurrentType === 'CONCURRENT')),
    [product]
  )
  const [timelineCollapsed, collapseTimeline] = useState(true)

  const openCreateListingWhichAccountDialog = React.useCallback(
    (marketId: number, additionalQueryParams: {}) => {
      setWhichAccountMarketId(marketId)
      setWhichAccountAdditionalParams(additionalQueryParams)
      setWhichAccountDialogOpen(true)
    },
    [setWhichAccountMarketId, setWhichAccountAdditionalParams, setWhichAccountDialogOpen]
  )

  const { permissions } = usePermissions()
  const supplierId = useSelector((s: AppState) => s.current.user.supplierId)

  if (loading) return <Spinner />
  if (error) {
    return <FullScreenErrorMessage iconName={IconNames.ERROR} message="" title={error} returnHome={false} />
  }

  if (notFound) return <NotFound />

  let variantsPriceRange = null
  if (product.variants?.length > 0) {
    const variantPrices = product.variants.map(v => v.price)
    variantsPriceRange = [Math.min(...variantPrices), Math.max(...variantPrices)]
  }

  const hasPriceOverride = !!product.overriddenValues
  const isRepricingActive = product.isMedia && !product.isRepricingPaused

  const disabledActions = hasPriceOverride || isRepricingActive
  const listDisabled = product.unitTracking === true || !!product.autoListStatus

  let disabledActionsMessage = null
  if (hasPriceOverride) {
    disabledActionsMessage = 'A price override is active, remove it to change the listing strategy'
  } else if (isRepricingActive) {
    disabledActionsMessage = 'This is being repriced'
  }

  return (
    <div className="product-drawer">
      <div className="item-header" style={styles.itemHeader}>
        {product.autoListStatus === 'failed' && (
          <Callout intent={Intent.DANGER} style={{ marginBottom: 5 }}>
            {getAutoListErrorMessage(product.tags)}
          </Callout>
        )}
        <div className="image-tiles">
          <ImageWithPlaceholder images={product.images} index={0} className="primary-image" />
          <ImageWithPlaceholder images={product.images} index={1} />
          <ImageWithPlaceholder images={product.images} index={2} />
        </div>
        <h5 className="drawer-header">{product.category && product.category.pathCache}</h5>
        <h2 className={Classes.TEXT_OVERFLOW_ELLIPSIS}>{product.title}</h2>
        <div className="details">
          <div className={CX('bp3-text-muted')}>Posted by: {product.user.username}</div>
          <InteractiveDatetime value={product.createdAt} />
          <div>
            <ProductStatusTag product={product} />
          </div>
        </div>
      </div>
      <div className="item-details">
        <div className="drawer-header">
          <Icon icon={IconNames.TAG} /> Item Details
          {!product.unitTracking && (
            <PrintBarcodeButton productId={product.id} productSkuLength={product.sku?.length} />
          )}
        </div>
        <div className="details">
          <GridSquare title="SKU" contents={product.sku} />
          <GridSquare title="Location" contents={product.inventoryLocation && product.inventoryLocation.name} />
          <GridSquare
            title="Product ID"
            contents={
              <CopySnippet
                // override-black is undefined in styles.scss on purpose, will inherit from parent
                className="override-black"
                value={product.id}
                toastValue="Product ID"
              />
            }
          />
          {product.recycledAt && (
            <GridSquare title="Recycled At" contents={<InteractiveDatetime value={product.recycledAt} />} />
          )}
          {config.PRODUCT_TAGS() && (
            <GridSquare
              title={
                <>
                  <span>Tags </span>
                  {permissions.includes('products.post') && (
                    <Button onClick={() => setTagDialogOpen(true)} icon="cog" small minimal />
                  )}
                </>
              }
              contents={
                <>
                  {product.listedByAi && <Tag intent={Intent.PRIMARY}>pearldive</Tag>}
                  {(product.tags || [])
                    .filter(t => t !== 'pearldive')
                    .map(t => (
                      <ProductTag title={t} key={t} />
                    ))}
                </>
              }
            />
          )}
          {config.SHOPIFY_VARIANTS() && <GridSquare title="In stock" contents={product.quantity?.toString()} />}
          <GridSquare title="Supplier" contents={product.supplier && product.supplier.name} />
          {product.isMedia && (
            <Tooltip content={formatDate(product.lastRepriceAt, true)}>
              <GridSquare title="Last Repricing" contents={product.lastRepriceAtTimeAgo} />
            </Tooltip>
          )}
        </div>
      </div>

      {config.SHOPIFY_VARIANTS() && <VariantDetails product={product} />}

      {product.unitTracking === true && (
        <InventoryUnitsDetails
          inventoryUnits={(product.inventoryUnits || []).map(u => ({
            ...u,
            isMedia: product.isMedia
          }))}
        />
      )}

      <div className="manifest-details">
        <div className="drawer-header">
          <Icon icon={IconNames.CLIPBOARD} /> Manifest Details
        </div>
        {!product.manifest && <div className="non-ideal">There is no manifest associated with this product.</div>}
        {product.manifest && (
          <div className="details">
            <GridSquare
              title="Barcode"
              contents={product.manifest.barcode && <ManifestLink data={product.manifest} />}
            />
            <GridSquare title="Supplier" contents={product.supplier && product.supplier.name} />
            <GridSquare
              title="Created by"
              contents={product.manifestItem && product.manifestItem.user && product.manifestItem.user.username}
            />
            <GridSquare
              title="Processed by"
              contents={product.manifestItem && product.manifestItem.handler && product.manifestItem.handler.username}
            />
            <GridSquare
              title="Processed at"
              contents={
                product.manifest &&
                product.manifest.processedAt && <InteractiveDatetime value={product.manifest.processedAt} />
              }
            />
          </div>
        )}
      </div>
      <TagEditorDialog
        isOpen={tagDialogOpen}
        onClose={result => {
          setTagDialogOpen(false)
          // TagEditorDialog now returns either an array of tags (legacy) or
          // a full product object. Handle both.
          if (Array.isArray(result)) {
            const newProduct = { ...product, tags: result }
            setProduct(newProduct)
            if (onProductChange) onProductChange(newProduct)
          } else if (result && result.id) {
            // full product returned from server
            setProduct(result)
            if (onProductChange) onProductChange(result)
          }
        }}
        tags={product.tags}
        productId={product.id}
      />

      <DuplicateDialog
        isOpen={duplicateDialogOpen}
        onClose={() => {
          setDuplicateDialogOpen(false)
        }}
        productIds={[product.id]}
        onDuplicateComplete={() => {
          reloadProduct()
          setDuplicateDialogOpen(false)
          onDuplicate()
        }}
      />

      <CreateListingWhichAccountDialog
        isOpen={whichAccountDialogOpen}
        onClose={() => {
          setWhichAccountDialogOpen(false)
        }}
        productId={product.id}
        marketId={whichAccountMarketId}
        additionalQueryParams={whichAccountAdditionalParams}
      />

      <OrderDetails product={product} />
      {!product.draft && permissions.includes('products.post') && (
        <StrategySection
          run={product.listingStrategyRun}
          reload={reloadProduct}
          productId={product?.id}
          marketAccountIds={product?.supplier?.marketAccountIds}
          concurrent={concurrent}
          isMedia={product.isMedia}
          isRepricingPaused={product.isRepricingPaused}
          disabledActions={disabledActions}
          disabledActionsMessage={disabledActionsMessage}
        />
      )}

      {!product.draft &&
        // Do not show this section if no product listings with no ability to create listings
        (product.productListings.length || permissions.includes('products.post')) && (
          <ListingHistorySection
            product={product}
            productPurgedAt={product.purgedAt}
            productRecycledAt={product.recycledAt}
            activeListings={product.activeListings}
            canonListings={product.canonListings}
            pastListings={product.pastListings}
            futureSteps={product.futureSteps}
            variantsPriceRange={variantsPriceRange}
            shopifyMarketAccounts={product.shopifyMarketAccounts}
            supplier={product.supplier}
            reload={reloadProduct}
            concurrent={concurrent}
            openCreateListingWhichAccountDialog={openCreateListingWhichAccountDialog}
          />
        )}
      {!supplierId && (
        <div>
          <div className="drawer-header">
            <Icon icon={IconNames.TIMELINE_EVENTS} /> Product Events
          </div>
          {!product.eventLogs ||
            (product.eventLogs.length === 0 && (
              <div className="non-ideal">No events to show. Please check back later</div>
            ))}

          {product.eventLogs && product.eventLogs.length > 0 && (
            <div style={{ marginLeft: -10 }}>
              <EventLogTimeline
                eventLogs={product.eventLogs}
                collapsed={product.eventLogs.length > 5 && timelineCollapsed}
                style={{ marginLeft: 10 }}
              />
              {product.eventLogs.length > 5 && (
                <Button
                  text={timelineCollapsed ? 'See More...' : 'See Less'}
                  intent="primary"
                  onClick={() => collapseTimeline(!timelineCollapsed)}
                  minimal
                />
              )}
            </div>
          )}
        </div>
      )}
      <div>
        <div className="drawer-header">
          <Icon icon={IconNames.COMMENT} /> Notes
        </div>
        <div style={productNotesStyle}>
          <Notes resource="Product" resourceId={product.id} />
        </div>
      </div>
      <div className="action-bar bp3-card bp3-elevation-2">
        {product.draft && permissions.includes('products.post') && (
          <DraftProductListActions
            productId={product.id}
            disabled={listDisabled}
            expressListProduct={product}
            variant="drawer"
          />
        )}
        {permissions.includes('products.post') && (
          <AnchorButton minimal icon={IconNames.EDIT} intent={Intent.PRIMARY} href={`/products/${product.id}/edit`}>
            <span className="action-text">Edit</span>
          </AnchorButton>
        )}
        {product.recycledAt && !hideRecycleRestore && !supplierId && (
          <AnchorButton
            minimal
            intent={Intent.WARNING}
            icon={IconNames.KEY_ESCAPE}
            onClick={() => onRestore(productId)}
          >
            <span className="action-text">Restore</span>
          </AnchorButton>
        )}
        {!product.draft && !product.recycledAt && !hideRecycleRestore && !supplierId && (
          <Tooltip content={!product.canRecycle && 'This product cannot be recycled while it has active listings'}>
            <AnchorButton
              disabled={!product.canRecycle}
              minimal
              icon={IconNames.REPEAT}
              intent={Intent.WARNING}
              onClick={() => onRecycle(productId)}
            >
              <span className="action-text">Recycle</span>
            </AnchorButton>
          </Tooltip>
        )}
        {permissions.includes('products.post') && (
          <AnchorButton
            minimal
            icon={IconNames.DUPLICATE}
            intent={Intent.WARNING}
            onClick={() => setDuplicateDialogOpen(true)}
            disabled={!!product.overriddenValues}
            title={
              product.overriddenValues ? 'A price override is active, remove it to duplicate the product' : undefined
            }
          >
            <span className="action-text">Duplicate</span>
          </AnchorButton>
        )}
        {product.draft && permissions.includes('products.inventory_management') && onDelete && (
          <AnchorButton minimal icon={IconNames.TRASH} intent={Intent.DANGER} onClick={() => onDelete(productId)}>
            <span className="action-text">Delete</span>
          </AnchorButton>
        )}
      </div>
    </div>
  )
}

export default ProductView
